import { Box, Paper, Typography } from '@mui/material';
import { ReactNode } from 'react';

interface StatCardProps {
  value: string | number;
  label: string;
  icon?: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
  compact?: boolean;
}

export default function StatCard({
  value,
  label,
  icon,
  color = 'primary',
  subtitle,
  compact = false,
}: StatCardProps) {
  const colorMap = {
    primary: 'primary.main',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    info: 'info.main',
  };

  const bgColorMap = {
    primary: 'primary.50',
    success: 'success.50',
    warning: 'warning.50',
    error: 'error.50',
    info: 'info.50',
  };

  if (compact) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        {icon && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: bgColorMap[color],
              color: colorMap[color],
              mb: 1,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h5" fontWeight={600} color={colorMap[color]}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.disabled">
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: bgColorMap[color],
            color: colorMap[color],
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h4" fontWeight={600} color={colorMap[color]} noWrap>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Typography
          variant="caption"
          color="text.disabled"
          noWrap
          sx={{ visibility: subtitle ? 'visible' : 'hidden' }}
        >
          {subtitle || '\u00A0'}
        </Typography>
      </Box>
    </Paper>
  );
}
